import React, { useMemo } from 'react';
import { formatMoney } from '../../utils/format';

/**
 * Linear-amortisation table. Pure presentational — takes principal, annual
 * rate and total months, computes the monthly schedule + totals. Used
 * inside `CreditsTab` for the expandable per-credit view. Extracted from
 * Finance.jsx on 28/05/2026.
 */
export default function AmortizationTable({ principal, annualRate, months }) {
  const rows = useMemo(() => {
    const result = [];
    let remaining = principal;
    const capitalPerMonth = principal / months;
    for (let i = 1; i <= months; i++) {
      const interest = remaining * (annualRate / 12);
      const payment = capitalPerMonth + interest;
      remaining = Math.max(0, remaining - capitalPerMonth);
      result.push({
        month: i,
        payment: Math.round(payment),
        capital: Math.round(capitalPerMonth),
        interest: Math.round(interest),
        remaining: Math.round(remaining),
      });
    }
    return result;
  }, [principal, annualRate, months]);

  const totalInterest = rows.reduce((s, r) => s + r.interest, 0);
  const totalPaid = rows.reduce((s, r) => s + r.payment, 0);

  return (
    <div className="mt-3 space-y-2" data-testid="amortization-table">
      <div className="bg-accent/10 rounded-lg p-3 grid grid-cols-3 gap-2 text-center">
        <div>
          <p className="text-[10px] text-slate-500">Capital</p>
          <p className="text-sm font-bold text-foreground">{formatMoney(principal)}</p>
        </div>
        <div>
          <p className="text-[10px] text-slate-500">Coût total intérêts</p>
          <p className="text-sm font-bold text-danger">{formatMoney(totalInterest)}</p>
        </div>
        <div>
          <p className="text-[10px] text-slate-500">Total remboursé</p>
          <p className="text-sm font-bold text-slate-300">{formatMoney(totalPaid)}</p>
        </div>
      </div>
      <div className="max-h-48 overflow-y-auto border border-ink-600 rounded-lg">
        <table className="w-full text-xs">
          <thead className="bg-ink-700/30 sticky top-0">
            <tr>
              <th className="px-2 py-1.5 text-left text-slate-500 font-medium">Mois</th>
              <th className="px-2 py-1.5 text-right text-slate-500 font-medium">Mensualité</th>
              <th className="px-2 py-1.5 text-right text-slate-500 font-medium">Capital</th>
              <th className="px-2 py-1.5 text-right text-slate-500 font-medium">Intérêts</th>
              <th className="px-2 py-1.5 text-right text-slate-500 font-medium">Restant</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.month} className="border-t border-ink-600">
                <td className="px-2 py-1 text-slate-400">{r.month}</td>
                <td className="px-2 py-1 text-right font-medium">{formatMoney(r.payment)}</td>
                <td className="px-2 py-1 text-right text-accent">{formatMoney(r.capital)}</td>
                <td className="px-2 py-1 text-right text-danger">{formatMoney(r.interest)}</td>
                <td className="px-2 py-1 text-right text-slate-500">{formatMoney(r.remaining)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
